import {
  BadgePercent, TrendingDown, Calculator, RefreshCw,
  UserCheck, Wrench, ShieldCheck, Building2, ArrowRight, FileText, Percent, Droplets,
} from "lucide-react";
import { Reveal, SectionHeading } from "../Reveal";
import { useLead } from "../../context/LeadContext";

const BENEFITS = [
  { icon: BadgePercent, text: "Attraktive KIA Flottenkonditionen" },
  { icon: TrendingDown, text: "Sonderkonditionen abhängig von Fahrzeug und Fuhrpark" },
  { icon: Calculator, text: "Individuelle Leasing- und Finanzierungslösungen" },
  { icon: RefreshCw, text: "Eintausch bestehender Fahrzeuge" },
  { icon: UserCheck, text: "Persönlicher Ansprechpartner" },
  { icon: Wrench, text: "Verkauf und Werkstatt an einem Standort" },
  { icon: ShieldCheck, text: "KIA Herstellergarantie gemäss aktuellen Schweizer Bedingungen" },
  { icon: Building2, text: "Lösungen für einzelne Geschäftsfahrzeuge bis zur Firmenflotte" },
];

const OFFERS = [
  { icon: Percent, title: "Bis zu 16 % Flottenrabatt", text: "auf KIA Neuwagen*" },
  { icon: Wrench, title: "1. Jahresservice geschenkt", text: "beim Kauf eines KIA Neuwagens" },
  { icon: Droplets, title: "Waschabo 1 Jahr gratis", text: "im Wert von CHF 600.– beim Kauf eines KIA Neuwagens" },
];

export default function Conditions() {
  const { requestOfferte } = useLead();

  return (
    <section id="konditionen" data-testid="conditions-section" className="py-24 sm:py-32 bg-neutral-950 text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div data-testid="offers-block" className="mb-16 sm:mb-20 bg-red-600 text-white p-8 sm:p-12">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-8">
              <h3 className="text-2xl sm:text-3xl font-medium tracking-tight">
                Ihre Vorteile beim Kauf bis 30.11.2026
              </h3>
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-white/80">Nur für kurze Zeit</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {OFFERS.map((o, i) => (
                <div key={o.title} data-testid={`offer-${i}`} className="flex items-start gap-5">
                  <span className="shrink-0 w-14 h-14 rounded-full bg-white flex items-center justify-center">
                    <o.icon size={24} className="text-red-600" />
                  </span>
                  <div>
                    <div className="text-lg font-semibold tracking-tight leading-snug">{o.title}</div>
                    <p className="mt-1.5 text-sm text-white/80 leading-relaxed">{o.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-[11px] leading-relaxed text-white/60">
              *Flottenrabatt abhängig von Modell, Flottengrösse und den jeweils gültigen KIA Flottenkonditionen.
              Aktion gültig beim Kauf eines KIA Neuwagens bis 30.11.2026.
            </p>
          </div>
        </Reveal>

        <SectionHeading
          dark
          number="02"
          label="Firmenkunden-Konditionen"
          title="Profitieren Sie als KIA Firmenkunde"
          sub="Unternehmen und Flottenkunden profitieren bei KIA von attraktiven Sonderkonditionen. Wir prüfen für Sie individuell, welche Firmenkundenkonditionen für Ihr Unternehmen und Ihr gewünschtes Fahrzeug möglich sind."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.text} delay={(i % 4) * 0.07}>
              <div
                data-testid={`condition-benefit-${i}`}
                className="border border-neutral-800 p-7 h-full flex flex-col gap-5 transition-colors duration-300 hover:border-neutral-600"
              >
                <b.icon size={22} strokeWidth={1.5} className="text-white" />
                <p className="text-sm leading-relaxed text-neutral-300">{b.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="conditions-cta-box"
            className="mt-16 bg-white text-neutral-950 p-8 sm:p-12 flex flex-col lg:flex-row lg:items-center gap-8 justify-between"
          >
            <div>
              <h3 className="text-2xl sm:text-3xl font-medium tracking-tight">
                Welche Firmenkonditionen erhalten Sie?
              </h3>
              <p className="mt-3 text-neutral-600 text-base max-w-xl leading-relaxed">
                Wir erstellen Ihnen kostenlos und unverbindlich eine individuelle Firmenofferte –
                abgestimmt auf Ihr Unternehmen und Ihr Wunschfahrzeug.
              </p>
            </div>
            <button
              data-testid="conditions-cta-button"
              onClick={() => requestOfferte()}
              className="group shrink-0 inline-flex items-center gap-3 bg-neutral-950 text-white font-semibold text-sm px-8 py-4 transition-colors hover:bg-neutral-800"
            >
              Firmenkonditionen anfragen
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-testid="flyer-downloads">
            <span className="text-neutral-500 text-xs uppercase tracking-[0.2em] font-semibold">Downloads</span>
            <a
              data-testid="flyer-front"
              href="https://customer-assets-cm19k8pv.emergentagent.net/job_kia-firmenkunden/artifacts/x9owj8li_KIA-A4-Frontseite.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white border-b border-white/60 pb-0.5 hover:opacity-70 transition-opacity"
            >
              <FileText size={15} /> KIA Elektro-Familie – Flyer (Vorderseite)
            </a>
            <a
              data-testid="flyer-back"
              href="https://customer-assets-cm19k8pv.emergentagent.net/job_kia-firmenkunden/artifacts/sjupb65f_KIA-A4-Ru%CC%88ckseite.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white border-b border-white/60 pb-0.5 hover:opacity-70 transition-opacity"
            >
              <FileText size={15} /> KIA Elektro-Familie – Flyer (Rückseite)
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
